import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, fmtDate } from "@/lib/api";
import { enablePush, currentPushSubscription, pushSupported } from "@/lib/push";

export function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [pushOn, setPushOn] = useState(false);
  const nav = useNavigate();

  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    currentPushSubscription().then((s) => setPushOn(!!s)).catch(() => {});
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, []);

  const onOpen = (o) => {
    if (o && data.unread) api.post("/notifications/read-all").then(() => setData((d) => ({ ...d, unread: 0 })));
  };
  const turnOnPush = async () => {
    try {
      await enablePush();
      setPushOn(true);
      toast.success("Push notifications enabled on this device");
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <Popover onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-xl text-[#0B2A4A] hover:bg-[#0B2A4A]/5" aria-label="Notifications" data-testid="notification-bell-btn">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-[#F08A3C] text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-unread-count">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <p className="font-display font-semibold">Alerts</p>
          {pushSupported() && !pushOn && (
            <button onClick={turnOnPush} className="text-xs font-semibold text-[#1A9AA8] inline-flex items-center gap-1" data-testid="enable-push-btn">
              <BellRing className="h-3.5 w-3.5" /> Enable push
            </button>
          )}
          {pushOn && <span className="text-xs text-emerald-700" data-testid="push-enabled-label">Push on</span>}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {data.items.length === 0 && <p className="px-4 py-6 text-sm text-slate-500">No alerts yet.</p>}
          {data.items.map((n) => (
            <button key={n.id} onClick={() => nav(n.url)} className={`w-full text-left px-4 py-3 border-b last:border-0 hover:bg-[#FAF6EE] ${n.read ? "" : "bg-[#1A9AA8]/5"}`} data-testid="notification-item">
              <p className="text-sm font-semibold text-[#0B2A4A]">{n.title}</p>
              <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.body}</p>
              <p className="text-[10px] text-slate-400 mt-1 font-mono-ui">{fmtDate(n.created_at)}</p>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
